import { DeleteOutlined, FileImageOutlined } from "@ant-design/icons"
import { Button, message, Upload } from "antd"
import { RcFile } from "antd/es/upload/interface"
import React, { useEffect, useState } from "react"
import { configs } from "../../../configs"
import { t } from "../../../languages/i18n"
import { addMedia } from "../../media/services/apiServices"

interface AppBackgroundData {
  backgroundImageUrl: string | null
}

interface SettingAppBackgroundProps {
  appBackgroundData: AppBackgroundData
  setAppBackgroundData: (data: AppBackgroundData) => void
}

const SettingAppBackground: React.FC<SettingAppBackgroundProps> = ({ 
  setAppBackgroundData, 
  appBackgroundData 
}) => {
  const [backgroundImageUrl, setBackgroundImageUrl] = useState<string | null>(
    appBackgroundData.backgroundImageUrl
  )
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    setBackgroundImageUrl(appBackgroundData.backgroundImageUrl)
  }, [appBackgroundData])

  useEffect(() => {
    if (backgroundImageUrl !== appBackgroundData.backgroundImageUrl) {
      setAppBackgroundData({
        backgroundImageUrl,
      })
    }
  }, [backgroundImageUrl, setAppBackgroundData, appBackgroundData.backgroundImageUrl])

  const handleUpload = async (file: RcFile): Promise<boolean> => {
    setLoading(true)
    const formData = new FormData()
    formData.append("files", file)

    try {
      const response = await addMedia(formData)

      if (!response || response.length === 0) {
        throw new Error("No response data")
      }

      setBackgroundImageUrl(`${configs.API_URL}${response[0].url}`)
      message.success(t("Background image uploaded successfully!"))
      return true
    } catch (error) {
      message.error(`Failed to upload background image: ${error}`)
      return false
    } finally {
      setLoading(false)
    }
  }

  const customRequest = async ({ file, onSuccess, onError }: any) => {
    try {
      const success = await handleUpload(file as RcFile)
      if (success) {
        onSuccess?.()
      } else {
        onError?.(new Error("Upload failed"))
      }
    } catch (error) {
      onError?.(error)
    }
  }

  const handleDelete = () => {
    setBackgroundImageUrl(null)
    message.success(t("Background image deleted successfully!"))
  }

  return (
    <div className="w-full pb-8 border-b flex flex-row justify-between items-center">
      <div>
        <p className="text-base font-semibold">App Background Image</p>
      </div>
      <div className="flex flex-col gap-2 items-center w-1/3">
        <div className="flex gap-2">
          <Upload accept="image/*" customRequest={customRequest} showUploadList={false} disabled={loading}>
            <Button icon={<FileImageOutlined />} loading={loading}>
              {t("Upload Background")}
            </Button>
          </Upload>
          {backgroundImageUrl && (
            <Button 
              icon={<DeleteOutlined />} 
              danger 
              onClick={handleDelete}
              disabled={loading}
            >
              {t("Delete")}
            </Button>
          )}
        </div>
        {backgroundImageUrl && (
          <div className="mt-4">
            <img 
              src={backgroundImageUrl} 
              alt="Background" 
              className="h-20 w-32 object-cover rounded border" 
            />
          </div>
        )}
      </div>
    </div>
  )
}

export default SettingAppBackground
